import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useGetEventsQuery } from "src/store/api/events";
import { selectCurrentWorkspaceId } from "src/store/app";

import { selectGroups } from "@dashboard/groups/store/selectors";
import { Chip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { skipToken } from "@reduxjs/toolkit/query";
import TimeService from "@services/timeService/timeService";
import { LoadingBox } from "@shared/Loading";
import { Modal } from "@shared/Modal/Modal";
import CustomTablePagination from "@shared/table/TablePagination";

import { ClickedPoint } from "../EventChartCard/EventChartCard.utils";

import { EventItem } from "./EventOverviewModal.components";
import { useStyles } from "./EventOverviewModal.styles";

type Props = {
    groupIds: number[];
    clickedPoint: ClickedPoint;
    onClose: () => void;
};

export const EventOverviewModal = (props: Props) => {
    const { classes } = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(8);

    const { t } = useTranslation(["modals"]);

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const allGroups = useSelector(selectGroups);
    const shouldSkip = !workspaceId;
    const { currentData: data, isFetching: isLoading } = useGetEventsQuery(
        shouldSkip
            ? skipToken
            : {
                  workspaceId: workspaceId,
                  groupIds: props.groupIds,
                  level: props.clickedPoint.hasOverlappingEvents ? undefined : props.clickedPoint.level,
                  createdFrom: TimeService.getStartOfDay(props.clickedPoint.epochMs),
                  createdTo: TimeService.getEndOfDay(props.clickedPoint.epochMs),
                  pageSize: rowsPerPage,
                  // BE index start from 1, so we add 1 to the page index
                  pageIndex: page + 1,
                  allWorkspaceGroupsLength: allGroups.length,
              },
    );

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Modal
            open
            maxWidth="md"
            fullWidth
            onClose={props.onClose}
            title={t("modals:eventModals.title")}
            titleSuffix={
                <Chip
                    icon={<i className="fa-regular fa-calendar"></i>}
                    label={`${TimeService.getLocalDate(props.clickedPoint.epochMs, "MILLIS", "date")}`}
                    variant="outlined"
                    size="small"
                />
            }
        >
            <div className={classes.scrollContainer}>
                {isLoading ? <LoadingBox className={classes.centeredBox} /> : null}
                {!isLoading && data?.list?.length
                    ? data?.list?.map((e) => <EventItem key={e.id} e={e} groupIds={props.groupIds} />)
                    : null}
                {!isLoading && !data?.list?.length ? (
                    <Box className={classes.centeredBox}>
                        <Typography className={classes.noActivityText}>{t("modals:eventModals.noActivity")}</Typography>
                    </Box>
                ) : null}
            </div>

            <CustomTablePagination
                component="div"
                rowsPerPageOptions={[8, 12, 24]}
                count={data?.count ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage="Rows"
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Modal>
    );
};
